import React, { useState, useEffect, useContext } from 'react';
import AppShell from '../../../components/layout/AppShell';
import { CheckIcon } from '@heroicons/react/20/solid';
import PropTypes from 'prop-types';
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline';
import { Disclosure } from '@headlessui/react';
import Spinner from '../../../components/layout/Spinner';
import LoadingModal from '../../../components/layout/LoadingModal';
import AlertPopup from '../../../components/layout/AlertPopUp';
import { createCheckoutSession } from '../../../services/subscriptionService';
import { UserContext } from '../../../contexts/UserContext';
import { Helmet } from 'react-helmet';

const tiers = [
  {
    name: 'Demo Plan',
    id: 'demo-plan',
    pricingId: 'price_1P9Hw8DhA7ZbzN8UALgb05DP',
    href: '#',
    priceMonthly: '0',
    description: '',
    features: [
      '2 Individually specific CVs',
      'Third-person conversion',
      'Format standardisation',
      'Resume personalisation',
      'Branded CVs',
    ],
    mostPopular: false,
  },
  {
    name: 'Personal Plan',
    id: 'personal-plan',
    pricingId: 'price_1SSUVyDhA7ZbzN8U8tGNNmaU',
    href: '#',
    priceMonthly: '49.99',
    description: '',
    features: [
      '10 Individually specific CVs',
      'Third-person conversion',
      'Format standardisation',
      'Resume personalisation',
      'Branded CVs',
    ],
    mostPopular: true,
  },
  {
    name: 'Professional Plan',
    id: 'professional-plan',
    pricingId: 'price_1SSUY5DhA7ZbzN8U3Lbf7TOB',
    href: '#',
    priceMonthly: '499.99',
    description: '',
    features: [
      '120 Individually specific CVs',
      'Third-person conversion',
      'Format standardisation',
      'Resume personalisation',
      'Branded CVs',
    ],
    mostPopular: false,
  },
  {
    name: 'Corporate Plan',
    id: 'corporate-plan',
    pricingId: 'price_1SSUaQDhA7ZbzN8Uc6zskJ0E',
    href: '#',
    priceMonthly: '9999.00',
    description: '',
    features: [
      'Unlimited Individually specific CVs',
      'Up to 500 different users',
      'Format standardisation',
      'Resume personalisation',
      'Branded CVs',
    ],
    mostPopular: false,
  },
];

const faqs = [
  {
    question: 'What file formats do you support?',
    answer:
      'We support a wide range of file formats, including PDF, Word (.doc and .docx), and plain text. Simply upload your existing resume, and our app will handle the rest.',
  },
  {
    question: 'How does the resume personalisation feature work?',
    answer:
      "Our resume personalisation feature allows you to tailor your CV to specific job descriptions or requirements. Simply upload the relevant document, and our app will highlight the key skills and experiences that align with the employer's needs.",
  },
  {
    question: 'Is there a limit to the number of CVs I can create?',
    answer:
      'With our Professional plan, you can create and optimize up to 2000 CVs a month. We believe in providing you with the flexibility and freedom to create as many tailored resumes as you need.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards, including Visa, Mastercard, and American Express. Our payment process is secure and encrypted to ensure the safety of your personal information.',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Pricing = () => {
  const [success, setSuccess] = useState(null);
  const { user, loading: userLoading } = useContext(UserContext) || {};
  const [error, setError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      checkSubscriptionStatus();
    }
  }, []);

  const checkSubscriptionStatus = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');

    if (status === 'success') {
      setSuccess('Your subscription has been activated.');
    } else if (status === 'cancelled') {
      setError('You did not complete the checkout process. Please complete it to access the software.');
    }
  };

  const handleSubscription = async (pricingId) => {
    if (typeof window !== 'undefined') {
      if (user) {
        setError(null);
        setIsRedirecting(true);
        try {
          const currentUrl = window.location.href;
          const baseUrl = currentUrl.split('?')[0];

          const checkoutSessionUrl = await createCheckoutSession(
            user.email,
            pricingId,
            `${baseUrl}?session_id={CHECKOUT_SESSION_ID}`,
            `${baseUrl}?session_id={CHECKOUT_SESSION_ID}`
          );

          window.location.href = checkoutSessionUrl;
        } catch (error) {
          console.error('Error creating checkout session:', error);
          setIsRedirecting(false);
          if (error.response && error.response.data && error.response.data.error) {
            setError(error.response.data.error);
          } else {
            setError('An error occurred. Please try again later.');
          }
        }
      } else {
        window.location.href = '/login';
      }
    }
  };

const corporateTier = tiers.find((t) => t.id === 'corporate-plan');
  const otherTiers = tiers.filter((t) => t.id !== 'corporate-plan');
  const renderTierCard = (tier) => (
    <div
      key={tier.id}
      className={classNames(
        tier.mostPopular ? 'border-primaryBlue/40 shadow-md' : '',
        'public-card public-card-body flex h-full flex-col'
      )}
    >
      <div className="flex items-center justify-between gap-x-4">
        <h3
          id={tier.id}
          className={classNames(
            tier.mostPopular ? 'text-primaryBlue' : 'text-darkGrey',
            'text-lg font-semibold leading-8'
          )}
        >
          {tier.name}
        </h3>
        {tier.mostPopular ? (
          <p className="rounded-md border border-primaryBlue/20 bg-primaryBlue/5 px-2 py-1 text-xs font-semibold text-primaryBlue">
            Most popular
          </p>
        ) : null}
      </div>
      <p className="mt-4 text-sm leading-6 text-slateGrey">{tier.description}</p>
      <p className="mt-6 flex items-baseline gap-x-1">
        <span className="text-4xl font-bold tracking-tight text-darkGrey">{tier.priceMonthly}</span>
        <span className="text-sm font-semibold leading-6 text-slateGrey">/month</span>
      </p>
      <ul className="mt-8 space-y-3 text-sm leading-6 text-slateGrey">
        {tier.features.map((feature) => (
          <li key={feature} className="flex gap-x-3">
            <CheckIcon className="public-icon-sm" aria-hidden="true" />
            {feature}
          </li>
        ))}
      </ul>
      <button
        onClick={(e) => {
          e.preventDefault();
          handleSubscription(tier.pricingId);
        }}
        disabled={userLoading || isRedirecting}
        aria-describedby={tier.id}
        className={classNames(
          tier.mostPopular ? 'public-primary-button' : 'public-secondary-button',
          'mt-auto block w-full'
        )}
      >
        {userLoading ? <Spinner /> : user ? 'Choose plan and continue to secure payment' : 'Log in to choose this plan'}
      </button>
    </div>
  );

  return (
    <AppShell currentPage="Pricing" loginRequired={false}>
      <Helmet>
        <title>CVHQ | Pricing | CV Converter - AI-Powered Resume Tool</title>
        <meta
          name="description"
          content="Explore the pricing options for our AI-powered CV Converter. Create a professional, tailored resume for free and unlock additional features with our affordable subscription plans. Boost your job search without breaking the bank."
        />
        <meta
          name="keywords"
          content="pricing, CV converter pricing, resume converter pricing, free CV converter, free resume converter, AI-powered CV converter, AI-driven resume conversion, convert CV online, convert resume online, tailor CV, tailor resume, customize CV, customize resume, optimize CV, optimize resume, professional CV, professional resume, affordable CV tool, affordable resume tool, subscription plans"
        />
      </Helmet>

      <main className="public-page">
        <div className="public-page-shell public-section">
          <div className="space-y-16">
            <div className="public-page-hero">
              <h1 className="public-page-title">Pricing</h1>
              <p className="public-page-intro">
                Choose a plan and continue to secure payment through Stripe. Access to the application starts once your subscription is active.
              </p>
            </div>

            <div className="public-secondary-surface mx-auto max-w-4xl p-6 sm:p-8">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-5">
                  <p className="text-sm font-semibold text-darkGrey">Step 1</p>
                  <p className="mt-2 text-sm leading-6 text-slateGrey">Select the package that fits your usage.</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-5">
                  <p className="text-sm font-semibold text-darkGrey">Step 2</p>
                  <p className="mt-2 text-sm leading-6 text-slateGrey">Complete payment details securely in Stripe.</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-5">
                  <p className="text-sm font-semibold text-darkGrey">Step 3</p>
                  <p className="mt-2 text-sm leading-6 text-slateGrey">Return to CVHQ and start converting CVs immediately.</p>
                </div>
              </div>
            </div>

            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {otherTiers.map((tier) => renderTierCard(tier))}
              </div>
            </div>

            {corporateTier && (
              <div className="mx-auto max-w-6xl">
                {renderTierCard(corporateTier)}
              </div>
            )}

            <div className="public-card public-card-body mx-auto max-w-4xl">
              <h2 className="text-2xl font-bold leading-10 tracking-tight text-darkGrey">Frequently asked questions</h2>
              <dl className="mt-8 divide-y divide-slate-200">
                {faqs.map((faq) => (
                  <Disclosure as="div" key={faq.question} className="py-6">
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full flex-col items-start justify-between text-left text-darkGrey">
                          <div className="flex w-full items-center justify-between">
                            <span className="text-base font-semibold leading-7">{faq.question}</span>
                            <span className="ml-6 flex h-7 items-center">
                              {open ? (
                                <MinusSmallIcon className="h-6 w-6" aria-hidden="true" />
                              ) : (
                                <PlusSmallIcon className="h-6 w-6" aria-hidden="true" />
                              )}
                            </span>
                          </div>
                          <Disclosure.Panel as="dd" className="mt-2 w-full pr-12">
                            <p className="text-base leading-7 text-slateGrey">{faq.answer}</p>
                          </Disclosure.Panel>
                        </Disclosure.Button>
                      </>
                    )}
                  </Disclosure>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </main>

      {success && <AlertPopup message={success} type="success" onClose={() => setSuccess(null)} />}
      {error && <AlertPopup message={error} type="error" onClose={() => setError(null)} />}
      <LoadingModal
        open={isRedirecting}
        title="Preparing Stripe checkout"
        message="Please wait while we secure your plan and redirect you to Stripe."
      />
    </AppShell>
  );
};

Pricing.propTypes = {
  user: PropTypes.object,
};

export default Pricing;

